<template>
    <Card pageTitle="Loyalty Health">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_LOYALTY">
            <div class="health-wrap">
                <div class="page-head">
                    <h2>Loyalty Health</h2>
                    <p>Daily loyalty operations, open exceptions, and balance integrity checks.</p>
                </div>

                <div class="toolbar">
                    <label>
                        <span>Report date</span>
                        <input v-model="reportDate" type="date" class="native-input" />
                    </label>
                    <label>
                        <span>Manual adjustment threshold</span>
                        <input v-model="manualThreshold" type="number" min="1" step="1" class="native-input threshold-input" />
                    </label>
                    <Button @click="loadReport" :disabled="loading">{{ loading ? "Loading..." : "Refresh" }}</Button>
                </div>

                <div v-if="report" class="status-strip" :class="report.status">
                    <div>
                        <span class="summary-label">Status</span>
                        <strong>{{ report.status === "healthy" ? "Healthy" : "Needs attention" }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Report date</span>
                        <strong>{{ report.report_date }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Generated</span>
                        <strong>{{ formatDate(report.generated_at) }}</strong>
                    </div>
                </div>

                <div v-if="report" class="summary-grid">
                    <div>
                        <span class="summary-label">Balance mismatches</span>
                        <strong>{{ report.summary.balance_mismatches }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Open issues</span>
                        <strong>{{ report.summary.unresolved_reconciliation_issues }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Failed operations</span>
                        <strong>{{ report.summary.failed_operations }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Unusual adjustments</span>
                        <strong>{{ report.summary.unusual_manual_adjustments }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Expired lots</span>
                        <strong>{{ report.summary.expired_lots_processed }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Expired points</span>
                        <strong>{{ report.summary.points_expired }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Campaign grants</span>
                        <strong>{{ report.summary.campaign_grants_processed || 0 }}</strong>
                    </div>
                    <div>
                        <span class="summary-label">Campaign points</span>
                        <strong>{{ report.summary.campaign_points_granted || 0 }}</strong>
                    </div>
                </div>

                <div v-if="report" class="check-list">
                    <section v-for="check in report.checks" :key="check.key" class="check-panel" :class="check.status">
                        <header>
                            <div>
                                <h3>{{ check.label }}</h3>
                                <p>{{ check.description }}</p>
                            </div>
                            <span :class="['status-badge', check.status]">
                                {{ statusLabel(check.status) }}
                            </span>
                        </header>

                        <div class="check-meta">
                            <span>Count: {{ check.count }}</span>
                            <span v-if="check.points_expired !== undefined">Points: {{ check.points_expired }}</span>
                            <span v-if="check.points_granted !== undefined">Points granted: {{ check.points_granted }}</span>
                            <span v-if="check.threshold !== undefined">Threshold: {{ check.threshold }}</span>
                            <span v-if="typeSummary(check)">Types: {{ typeSummary(check) }}</span>
                        </div>

                        <table v-if="check.rows && check.rows.length" class="detail-table">
                            <thead>
                                <tr>
                                    <th>Primary</th>
                                    <th>Details</th>
                                    <th>When</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, index) in check.rows" :key="`${check.key}-${index}`">
                                    <td>{{ rowPrimary(check, row) }}</td>
                                    <td>{{ rowDetails(check, row) }}</td>
                                    <td>{{ formatDate(row.createdAt || row.created_at || row.updatedAt) }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-else class="empty compact">No rows for this check.</p>
                    </section>
                </div>

                <div v-if="!report && !loading" class="empty-state">
                    <strong>No report loaded</strong>
                    <span>Select a date and refresh.</span>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import LoyaltyFetchApi from "../../utils/fetch/loyalty-fetch-api"
import { errMsg } from "../../utils/common"

export default {
    components: { Card, Button, AllowPermission },
    data() {
        return {
            PERMISSION_CODES,
            report: null,
            reportDate: this.today(),
            manualThreshold: 100,
            loading: false,
        }
    },
    async mounted() {
        await this.loadReport()
    },
    methods: {
        today() {
            const now = new Date()
            const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            return local.toISOString().slice(0, 10)
        },
        async loadReport() {
            this.loading = true
            try {
                this.report = await LoyaltyFetchApi.fetchHealthReport(this.reportDate, this.manualThreshold)
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load loyalty health report"))
            }
            this.loading = false
        },
        statusLabel(status) {
            if (status === "attention") return "Needs attention"
            if (status === "info") return "Info"
            return "OK"
        },
        typeSummary(check) {
            return Object.entries(check.by_type || {})
                .map(([type, count]) => `${type}: ${count}`)
                .join(", ")
        },
        rowPrimary(check, row) {
            if (check.key === "balance_mismatches") return row.customer_name || row.customer_id || row.account_id
            if (row.type) return row.type
            if (row.customer_id) return row.customer_id
            return row.id || "-"
        },
        rowDetails(check, row) {
            if (check.key === "balance_mismatches") {
                return `Account ${row.account_balance}; lots ${row.lot_balance}; delta ${row.delta}`
            }
            if (check.key === "expired_lots_processed") {
                return `${row.points_delta || 0} points; balance after ${row.balance_after || 0}; lot ${row.lot_id || "-"}`
            }
            if (check.key === "unusual_manual_adjustments") {
                return `${row.points_delta || 0} points; ${row.performed_by_name || "Unknown user"}; ${row.reason || "-"}`
            }
            if (check.key === "campaign_grants_processed") {
                return `${row.points_granted || 0} points; campaign ${row.campaign_id || "-"}; ${row.grant_reason || "-"}`
            }
            if (row.error || row.unrecovered_points !== undefined) {
                return `${row.ref_id || "-"}; unrecovered ${row.unrecovered_points || 0}; ${row.error || "-"}`
            }
            return row.reason || row.ref_id || "-"
        },
        formatDate(value) {
            if (!value) return "-"
            return new Date(value).toLocaleString()
        },
    },
}
</script>

<style lang="scss" scoped>
.health-wrap { max-width: 1180px; }
.page-head { margin-bottom: 20px; }
.page-head h2 { margin: 0 0 6px; font-size: 24px; font-weight: 700; color: #0b302c; }
.page-head p { margin: 0; color: #5f6368; line-height: 1.45; }
.toolbar { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 12px; margin-bottom: 18px; }
.toolbar label { display: grid; gap: 6px; font-weight: 700; color: #374151; }
.native-input { min-height: 40px; border: 1px solid #d9dee3; border-radius: 6px; padding: 8px 10px; background: #fff; min-width: 170px; }
.threshold-input { max-width: 160px; }
.status-strip, .summary-grid { display: grid; gap: 12px; margin-bottom: 18px; }
.status-strip { grid-template-columns: 150px 160px minmax(220px, 1fr); border: 1px solid #d8e5e2; border-radius: 8px; padding: 14px; background: #f7fbfa; }
.status-strip.attention { border-color: #f3d28a; background: #fff9e8; }
.summary-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.summary-grid > div { border: 1px solid #e3e6ea; border-radius: 8px; padding: 12px; background: #fff; }
.summary-label { color: #80868b; font-size: 12px; font-weight: 800; text-transform: uppercase; }
.status-strip strong, .summary-grid strong { display: block; margin-top: 4px; color: #0b302c; font-size: 18px; }
.check-list { display: grid; gap: 14px; }
.check-panel { border: 1px solid #e3e6ea; border-radius: 8px; padding: 14px; background: #fff; }
.check-panel.attention { border-color: #f0c36b; }
.check-panel.info { border-color: #b9d8f5; }
.check-panel header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; margin-bottom: 10px; }
.check-panel h3 { margin: 0 0 4px; color: #111827; font-size: 16px; font-weight: 800; }
.check-panel p { margin: 0; color: #6b7280; line-height: 1.4; }
.check-meta { display: flex; flex-wrap: wrap; gap: 8px 16px; margin-bottom: 10px; color: #374151; font-weight: 700; }
.status-badge { display: inline-flex; align-items: center; border-radius: 999px; padding: 4px 9px; font-size: 12px; font-weight: 800; white-space: nowrap; }
.status-badge.ok { background: #e8f5ec; color: #1f7a3b; }
.status-badge.info { background: #e7f2ff; color: #215b8f; }
.status-badge.attention { background: #fff3d6; color: #9a5b00; }
.detail-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.detail-table th, .detail-table td { text-align: left; padding: 8px; border-top: 1px solid #edf0f2; vertical-align: top; overflow-wrap: anywhere; }
.detail-table th { color: #4b5563; white-space: nowrap; }
.empty { color: #80868b; }
.empty.compact { margin: 0; }
.empty-state { min-height: 140px; display: flex; flex-direction: column; justify-content: center; gap: 6px; color: #6b7280; border-top: 1px solid #edf0f2; }
.empty-state strong { color: #111827; font-size: 16px; }
@media (max-width: 1000px) {
    .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .status-strip { grid-template-columns: 1fr; }
}
</style>
